// Commercial data of the current organization (ADR-0047). Everything is computed by the server.
import { useQuery } from "@tanstack/react-query";
import { api, call } from "@/api/client";
import { ApiProblem } from "@/api/errors";

export const commercialKeys = {
  company: (orgId: string) => ["org", orgId, "company"] as const,
  purchaseOrders: (orgId: string) => ["org", orgId, "purchase-orders"] as const,
  authorizations: (orgId: string, campaignId?: string) =>
    ["org", orgId, "campaign-authorizations", campaignId ?? "all"] as const,
};

/** The organization's company, terms and credit; null when the caller may not read it (not an admin). */
export function useCompany(orgId: string) {
  return useQuery({
    queryKey: commercialKeys.company(orgId),
    queryFn: async () => {
      try {
        return await call(() => api.GET("/orgs/{orgId}/company", { params: { path: { orgId } } }));
      } catch (error) {
        if (error instanceof ApiProblem && error.status === 403) return null;
        throw error;
      }
    },
    staleTime: 30_000,
  });
}

export function usePurchaseOrders(orgId: string, enabled = true) {
  return useQuery({
    queryKey: commercialKeys.purchaseOrders(orgId),
    queryFn: () => call(() => api.GET("/orgs/{orgId}/purchase-orders", { params: { path: { orgId } } })),
    enabled,
  });
}

export function useAuthorizations(orgId: string, campaignId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: commercialKeys.authorizations(orgId, campaignId),
    queryFn: () =>
      call(() =>
        api.GET("/orgs/{orgId}/campaign-authorizations", {
          params: { path: { orgId }, query: campaignId ? { campaignId } : {} },
        })
      ),
    enabled,
  });
}
